import { useCallback } from 'react';
import { useIntl, type FormatRelativeTimeOptions } from '@olxpwa-lib/i18n/client';
import { dayjs } from '~utils/date';

export const useFormatRelativeDate = () => {
  const { formatMessage, formatRelativeTime, formatDate } = useIntl();

  return useCallback(
    (date: Date | number | string): string => {
      const options: FormatRelativeTimeOptions = { numeric: 'always' };
      const minutes = dayjs(date).diff(undefined, 'minutes');

      if (minutes === 0) {
        const isInFuture = dayjs(date).diff(undefined) > 0;

        return isInFuture
          ? formatMessage({
              id: 'comp.date.in_a_moment',
              defaultMessage: 'soon',
            })
          : formatMessage({
              id: 'comp.date.just_now',
              defaultMessage: 'just now',
            });
      }

      const hours = dayjs(date).diff(undefined, 'hours');

      if (hours === 0) {
        return formatRelativeTime(minutes, 'minutes', options);
      }

      const days = dayjs(date).diff(undefined, 'days');

      if (days === 0) {
        return formatRelativeTime(hours, 'hours', options);
      }

      const weeks = dayjs(date).diff(undefined, 'weeks');

      if (weeks === 0) {
        return formatRelativeTime(days, 'days', options);
      }

      const months = dayjs(date).diff(undefined, 'months');

      if (months === 0) {
        return formatRelativeTime(weeks, 'weeks', options);
      }

      const years = dayjs(date).diff(undefined, 'years');

      if (years === 0) {
        return formatRelativeTime(months, 'months', options);
      }

      return formatDate(date, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    },
    [formatMessage, formatRelativeTime, formatDate],
  );
};
